import axios from "axios";
import { API_URL } from "../config";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  FileText,
  Eye,
  Send,
  XCircle,
  Download,
  Clock,
  PenTool,
  Network,
  CheckCircle2,
  Layers,
  RefreshCw,
  Bell,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 10;
const POLL_MS = 20000; // refresh requests + notifications every 20s

const TYPE_OPTIONS = ["All", "Signing", "Approval"];
const PRIORITY_OPTIONS = ["All", "High", "Medium", "Low"];
const TABS = [
  { key: "all", label: "All" },
  { key: "mine", label: "Assigned to me" },
  { key: "sent", label: "Sent by me" },
];

// Which requests each tab shows; any unknown tab falls back to "Sent by me".
const TAB_FILTERS = {
  all: () => true,
  mine: (r) => r.myStep != null,
  sent: (r) => !!r.isSender,
};
const tabFilter = (key) => TAB_FILTERS[key] || TAB_FILTERS.sent;

const TYPE_ICONS = {
  Signing: { Icon: PenTool, color: "text-indigo-600" },
  Approval: { Icon: Network, color: "text-green-600" },
};

const PRIORITY_STYLES = {
  High: "bg-red-50 text-red-600",
  Medium: "bg-yellow-50 text-yellow-700",
  Low: "bg-green-50 text-green-600",
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const getStatusClasses = (status = "") => {
  if (status === "Completed" || status === "Document Signed" || status.endsWith("by you")) return "bg-green-50 text-green-600";
  if (status === "Rejected") return "bg-red-50 text-red-500";
  if (status === "Cancelled" || status === "Expired") return "bg-gray-100 text-gray-500";
  if (status.startsWith("Pending")) return "bg-amber-50 text-amber-600";
  return "bg-blue-50 text-blue-600"; // Awaiting X / In Progress
};

const formatDate = (v) => {
  const d = new Date(v);
  if (!v || isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
};

const timeAgo = (v) => {
  const secs = Math.floor((Date.now() - new Date(v).getTime()) / 1000);
  if (isNaN(secs)) return "";
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
  return `${Math.floor(secs / 86400)} d ago`;
};

const errMsg = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  const base = fallback.replace(/\.$/, "");
  const status = err?.response?.status;
  return status
    ? `${base} (the server returned ${status}).`
    : `${base} (could not reach the server).`;
};

/* ------------------------------------------------------------------ */
/* Small components                                                    */
/* ------------------------------------------------------------------ */

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-gray-600">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full min-w-[100px] border border-gray-200 rounded-lg pl-2.5 pr-7 h-8 text-xs text-gray-700 bg-white outline-none focus:border-indigo-400 cursor-pointer"
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, hint, Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2.5 min-w-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon size={16} className={iconColor} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{title}</p>
        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-[11px] text-gray-400 truncate">{hint}</p>
      </div>
    </div>
  );
}

function ProgressCell({ row, active }) {
  const pct = row.totalSteps ? Math.round((row.doneSteps / row.totalSteps) * 100) : 0;
  const yourStep = row.myStep && row.signingOrder ? ` • your step ${row.myStep}` : "";
  const barColor = row.requestStatus === "completed" ? "bg-green-500" : "bg-indigo-500";
  return (
    <div className="w-[120px]">
      <p className="text-[11px] text-gray-600 mb-1">
        {row.doneSteps} of {row.totalSteps} done
        {yourStep}
      </p>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {active && row.waitingOn && (
        <p className="text-[11px] text-gray-400 mt-1 truncate">Waiting on {row.waitingOn}</p>
      )}
    </div>
  );
}

const MENU_ITEM_CLASS = "w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700 flex items-center gap-2";

function RowMenu({ row, active, onOpen, onDownload, onRemind, onCancel, onClose }) {
  // close the menu first, then run the chosen action
  const choose = (fn) => () => {
    onClose();
    fn(row);
  };
  return (
    <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-xl shadow-lg w-48 py-1 text-sm">
      <button onClick={choose(onOpen)} className={MENU_ITEM_CLASS}>
        <Eye size={13} />
        View Details
      </button>

      <button onClick={choose(onDownload)} className={MENU_ITEM_CLASS}>
        <Download size={13} />
        Download
      </button>

      {row.isSender && active && (
        <>
          <button onClick={choose(onRemind)} className={MENU_ITEM_CLASS}>
            <Send size={13} />
            Send Reminder
          </button>

          <div className="border-t border-gray-100 my-1" />

          <button
            onClick={choose(onCancel)}
            className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-500 flex items-center gap-2"
          >
            <XCircle size={13} />
            Cancel Request
          </button>
        </>
      )}
    </div>
  );
}

function RequestRow({
  row, checked, onToggle, onOpen, menuOpen, menuRef, onToggleMenu, onCloseMenu, onDownload, onRemind, onCancel,
}) {
  const t = TYPE_ICONS[row.type] || TYPE_ICONS.Signing;
  const TypeIcon = t.Icon;
  const active = row.requestStatus === "in_progress";
  const reviewClass = row.canAct
    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
    : "bg-white border border-indigo-300 text-indigo-600 hover:bg-indigo-50";

  return (
    <tr className={`transition ${row.canAct ? "bg-indigo-50/40" : "hover:bg-gray-50"}`}>
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(row.id)}
          className="w-4 h-4 accent-indigo-600 cursor-pointer"
        />
      </td>

      <td className="px-2 py-1.5 whitespace-nowrap">
        <button onClick={() => onOpen(row)} className="text-xs font-medium text-indigo-600 hover:underline">
          {row.id}
        </button>
      </td>

      <td className="px-2 py-1.5 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <TypeIcon size={15} className={t.color} />
          <span className="text-xs text-gray-700">{row.type}</span>
        </div>
      </td>

      <td className="px-2 py-1.5">
        <p className="text-xs font-medium text-gray-800 truncate max-w-[150px]">{row.document}</p>
        {row.size && <p className="text-[11px] text-gray-400">Size: {row.size}</p>}
      </td>

      <td className="px-2 py-1.5">
        <p className="text-xs font-medium text-gray-800">{row.requester || "—"}</p>
        {row.requesterEmail && <p className="text-[11px] text-gray-400">{row.requesterEmail}</p>}
      </td>

      <td className="px-2 py-1.5">
        <ProgressCell row={row} active={active} />
      </td>

      <td className="px-2 py-1.5 text-xs text-gray-600 whitespace-nowrap">{formatDate(row.submittedOn)}</td>

      <td className="px-2 py-1.5">
        <span
          className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-medium ${
            PRIORITY_STYLES[row.priority] || "bg-gray-100 text-gray-500"
          }`}
        >
          {row.priority}
        </span>
      </td>

      <td className="px-2 py-1.5">
        <span
          className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-medium whitespace-nowrap ${getStatusClasses(
            row.status
          )}`}
        >
          {row.status}
        </span>
      </td>

      {/* Actions */}
      <td className="px-2 py-1.5">
        <div className="relative flex items-center gap-2" ref={menuOpen ? menuRef : null}>
          <button
            onClick={() => onOpen(row)}
            className={`px-3 h-7 text-xs font-medium rounded-md transition ${reviewClass}`}
          >
            {row.canAct ? "Review" : "View"}
          </button>

          <button
            onClick={onToggleMenu}
            className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-500 transition"
          >
            <MoreVertical size={15} />
          </button>

          {menuOpen && (
            <RowMenu
              row={row}
              active={active}
              onOpen={onOpen}
              onDownload={onDownload}
              onRemind={onRemind}
              onCancel={onCancel}
              onClose={onCloseMenu}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function RequestStatusPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user")) || {}, []);
  const email = user.email;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [notifications, setNotifications] = useState([]);
  const [showBell, setShowBell] = useState(false);
  const [toast, setToast] = useState(null);
  const seenRef = useRef(null); // ids of notifications already known
  const toastTimer = useRef(null);
  const bellRef = useRef(null);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  /* ---------- data ---------- */
  const showToast = (message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  };

  const applyNotifications = (data) => {
    const items = data?.items || [];
    if (seenRef.current) {
      // anything new and unread that arrived since the last poll → pop a toast
      const fresh = items.filter((n) => !seenRef.current.has(n.id) && !n.isRead);
      if (fresh.length) showToast(fresh[0].message);
    }
    seenRef.current = new Set(items.map((n) => n.id));
    setNotifications(items);
  };

  const fetchAll = useCallback(
    async (silent = false) => {
      if (!email) return;
      if (!silent) setLoading(true);
      try {
        const [reqRes, notifRes] = await Promise.all([
          axios.get(`${API_URL}/api/workflow/requests`, { params: { user_email: email } }),
          axios.get(`${API_URL}/api/workflow/notifications`, { params: { user_email: email } }),
        ]);
        setRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
        applyNotifications(notifRes.data);
        setLoadError("");
      } catch (err) {
        console.error("REQUESTS LOAD ERROR:", err);
        if (!silent) setLoadError(errMsg(err, "Could not load requests."));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [email]
  );

  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => fetchAll(true), POLL_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(toastTimer.current);
    };
  }, [fetchAll]);

  // close menus on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
      if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, search, typeFilter, statusFilter, priorityFilter]);

  /* ---------- notifications ---------- */
  const unread = notifications.filter((n) => !n.isRead).length;

  const openNotification = async (n) => {
    setShowBell(false);
    if (!n.isRead) {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      try {
        await axios.post(`${API_URL}/api/workflow/notifications/${n.id}/read`, { user_email: email });
      } catch (err) {
        console.error(err);
      }
    }
    navigate(`/request-details/${n.requestId}`);
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((x) => ({ ...x, isRead: true })));
    try {
      await axios.post(`${API_URL}/api/workflow/notifications/read-all`, { user_email: email });
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- derived ---------- */
  const inTab = useMemo(() => requests.filter(tabFilter(tab)), [requests, tab]);

  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(requests.map((r) => r.status)))],
    [requests]
  );

  const stats = useMemo(() => {
    const active = requests.filter((r) => r.requestStatus === "in_progress");
    return [
      {
        title: "Awaiting My Action",
        value: requests.filter((r) => r.canAct).length,
        hint: "Your turn now",
        Icon: Clock,
        iconBg: "bg-indigo-50",
        iconColor: "text-indigo-600",
      },
      {
        title: "Signing Requests",
        value: active.filter((r) => r.type === "Signing").length,
        hint: "In progress",
        Icon: PenTool,
        iconBg: "bg-blue-50",
        iconColor: "text-blue-600",
      },
      {
        title: "Approval Requests",
        value: active.filter((r) => r.type === "Approval").length,
        hint: "In progress",
        Icon: Network,
        iconBg: "bg-red-50",
        iconColor: "text-red-500",
      },
      {
        title: "Sent By Me",
        value: requests.filter((r) => r.isSender && r.requestStatus === "in_progress").length,
        hint: "Still in progress",
        Icon: Layers,
        iconBg: "bg-sky-50",
        iconColor: "text-sky-600",
      },
      {
        title: "Completed",
        value: requests.filter((r) => r.requestStatus === "completed").length,
        hint: "All steps done",
        Icon: CheckCircle2,
        iconBg: "bg-green-50",
        iconColor: "text-green-600",
      },
    ];
  }, [requests]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return inTab
      .filter((r) => {
        const matchSearch =
          !q ||
          r.id.toLowerCase().includes(q) ||
          (r.document || "").toLowerCase().includes(q) ||
          (r.requester || "").toLowerCase().includes(q) ||
          (r.requesterEmail || "").toLowerCase().includes(q);
        return (
          matchSearch &&
          (typeFilter === "All" || r.type === typeFilter) &&
          (statusFilter === "All" || r.status === statusFilter) &&
          (priorityFilter === "All" || r.priority === priorityFilter)
        );
      })
      .sort((a, b) => {
        if (a.canAct !== b.canAct) return a.canAct ? -1 : 1; // "your turn" first
        return new Date(b.submittedOn) - new Date(a.submittedOn);
      });
  }, [inTab, search, typeFilter, statusFilter, priorityFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);
  const pageIds = pageRows.map((r) => r.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  /* ---------- handlers ---------- */
  const clearFilters = () => {
    setSearch("");
    setTypeFilter("All");
    setStatusFilter("All");
    setPriorityFilter("All");
  };

  const toggleAll = () =>
    setSelected((prev) =>
      allOnPageSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...prev, ...pageIds]))
    );

  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const openRequest = (row) => navigate(`/request-details/${row.id}`);

  const downloadFile = (row) =>
    window.open(
      `${API_URL}${row.fileUrl}?download=1&user_email=${encodeURIComponent(email)}`,
      "_blank"
    );

  const sendReminder = async (row) => {
    try {
      const res = await axios.post(`${API_URL}/api/workflow/requests/${row.id}/remind`, { user_email: email });
      showToast(res.data?.message || "Reminder sent");
    } catch (err) {
      alert(errMsg(err, "Could not send the reminder"));
    }
  };

  const cancelRequest = async (row) => {
    if (!window.confirm("Cancel this request? Nobody will be able to act on it anymore.")) return;
    try {
      await axios.delete(`${API_URL}/api/workflow/requests/${row.id}`, { params: { user_email: email } });
      fetchAll(true);
    } catch (err) {
      alert(errMsg(err, "Could not cancel the request"));
    }
  };

  const pageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    return Array.from({ length: 5 }, (_, i) => start + i);
  };

  /* ---------- render ---------- */
  return (
    <div style={{ fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif" }} className="min-w-0">
      <div className="mx-auto min-w-0 space-y-3">
        {/* TITLE + NOTIFICATION BELL */}
        <div className="flex items-center justify-end">
          <div className="relative" ref={bellRef}>
            <button
              onClick={() => setShowBell((v) => !v)}
              className="relative w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition"
              aria-label="Notifications"
            >
              <Bell size={15} className="text-gray-600" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>

            {showBell && (
              <div className="absolute right-0 top-12 z-30 w-[360px] max-w-[90vw] bg-white border border-gray-200 rounded-xl shadow-lg">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Notifications</p>
                  {unread > 0 && (
                    <button onClick={markAllRead} className="text-xs text-indigo-600 hover:underline">
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-100">
                  {notifications.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</p>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => openNotification(n)}
                        className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition ${
                          n.isRead ? "" : "bg-indigo-50/50"
                        }`}
                      >
                        <span
                          className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                            n.isRead ? "bg-transparent" : "bg-indigo-500"
                          }`}
                        />
                        <span className="min-w-0">
                          <span className="block text-xs text-gray-800 break-words">{n.message}</span>
                          <span className="block text-[11px] text-gray-400 mt-0.5">
                            {n.requestId} • {timeAgo(n.createdAt)}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {stats.map((s) => (
            <StatCard key={s.title} {...s} />
          ))}
        </div>

        {/* MAIN CARD */}
        <div className="border border-gray-200 rounded-xl bg-white">
          {/* TABS */}
          <div className="flex items-center gap-5 px-3 border-b border-gray-100 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`py-2 text-xs font-medium border-b-2 whitespace-nowrap transition ${
                  tab === t.key
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label} <span className="text-xs text-gray-400 ml-1">{requests.filter(tabFilter(t.key)).length}</span>
              </button>
            ))}
          </div>

          {/* FILTER ROW */}
          <div className="flex items-end gap-2 p-3 flex-wrap">
            <div className="flex-1 min-w-[180px] relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" />
              <input
                type="text"
                placeholder="Search by ID, file name, or requester..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 h-8 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white outline-none focus:border-indigo-400 transition"
              />
            </div>

            <FilterSelect label="Request Type" value={typeFilter} onChange={setTypeFilter} options={TYPE_OPTIONS} />
            <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={statusOptions} />
            <FilterSelect label="Priority" value={priorityFilter} onChange={setPriorityFilter} options={PRIORITY_OPTIONS} />

            <button
              onClick={clearFilters}
              className="h-8 px-3 border border-indigo-300 text-indigo-600 rounded-lg text-xs font-medium hover:bg-indigo-50 transition"
            >
              Clear Filters
            </button>

            <button
              onClick={() => fetchAll()}
              disabled={loading}
              className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {loadError && (
            <p className="mx-3 mb-2 text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {loadError}
            </p>
          )}

          {/* TABLE */}
          <div className="overflow-x-auto px-3">
            <table className="w-full min-w-[880px]">
              <thead>
                <tr className="border-y border-gray-100 bg-gray-50/60">
                  <th className="w-8 px-2 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-indigo-600 cursor-pointer"
                    />
                  </th>
                  {["ID", "Type", "Document / Resource", "Requester", "Progress", "Submitted On", "Priority", "Status", "Actions"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {pageRows.map((row) => (
                  <RequestRow
                    key={row.id}
                    row={row}
                    checked={selected.includes(row.id)}
                    onToggle={toggleOne}
                    onOpen={openRequest}
                    menuOpen={openMenu === row.id}
                    menuRef={menuRef}
                    onToggleMenu={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                    onCloseMenu={() => setOpenMenu(null)}
                    onDownload={downloadFile}
                    onRemind={sendReminder}
                    onCancel={cancelRequest}
                  />
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <FileText size={20} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-400 font-medium">
                          {loading ? "Loading requests..." : "No requests found"}
                        </p>
                        {!loading && (
                          <p className="text-xs text-gray-300">
                            Requests you send, or that are waiting for you, will appear here
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2.5">
            <p className="text-xs text-gray-500">
              Showing {filtered.length === 0 ? 0 : startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} of{" "}
              {filtered.length} request{filtered.length !== 1 ? "s" : ""}
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={15} />
              </button>

              {pageNumbers().map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`w-7 h-7 rounded-md text-xs border transition ${
                    n === currentPage
                      ? "bg-indigo-600 border-indigo-600 text-white font-medium"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              ))}

              <button
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TOAST for new notifications */}
      {toast && (
        <button
          onClick={() => {
            setToast(null);
            setShowBell(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="fixed bottom-6 right-6 z-40 max-w-sm bg-gray-900 text-white text-xs rounded-xl shadow-lg px-4 py-3 flex items-start gap-3 text-left"
        >
          <Bell size={15} className="mt-0.5 shrink-0" />
          <span>{toast}</span>
        </button>
      )}
    </div>
  );
}